import React, { useEffect, useMemo, useState, useCallback } from "react";
import DataTable from "../../../../../../Common/DataTable/DataTable";
import DataTableHeader from "../../../../../../Common/DataTableHeader/DataTableHeader";
import DataTableFooter from "../../../../../../Common/DataTableFooter/DataTableFooter";
import { leaveRequestApi } from "../../../../../../api/leaveRequestApi";
import axios from "axios";
import { REACT_APP_API_URL } from "../../../../../../env";
import { generateLeaveCancellationPDF } from "../../../../../../Partials/Reports/Components/PurchaseReports/Components/leave/LeaveCancellationPrint";


const LeaveCancellationRequestTable = ({ onDecision }) => {
    const [rows, setRows] = useState([]);
    const [exportRows, setExportRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [selectedDecision, setSelectedDecision] = useState(null);
    const [adminRemarks, setAdminRemarks] = useState("");
    const [showRemarksModal, setShowRemarksModal] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);

    const tableData = useMemo(() => rows, [rows]);

    const formatDate = (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            timeZone: "UTC",
        });
    };

    const renderActions = (requestId) => {

        const handleApprove = async () => {
            try {
                setUpdatingId(requestId);

                const response = await leaveRequestApi.decideCancellation(requestId, {
                    decision: "approve",
                    remarks: null,
                });

                window.alert(
                    response?.data?.message ||
                    "Leave Cancellation approved successfully"
                );

                setRows(prev => prev.filter(r => r.__id !== requestId));

                if (onDecision) onDecision();


            } catch (err) {
                setError(err?.message || "Failed to approve cancellation");
            } finally {
                setUpdatingId(null);
            }
        };

        const openRejectModal = () => {
            setSelectedRequestId(requestId);
            setSelectedDecision("reject");
            setAdminRemarks("");
            setShowRemarksModal(true);
        };

        return (
            <div className="btn-group">
                <button
                    className="btn btn-outline-success btn-sm"
                    onClick={handleApprove}
                    disabled={updatingId === requestId}
                    title="Approve Cancellation"
                >
                    <i className="bi bi-check2"></i>
                </button>

                <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={openRejectModal}
                    disabled={updatingId === requestId}
                    title="Reject Cancellation"
                >
                    <i className="bi bi-x"></i>
                </button>
            </div>
        );
    };

    const handleDecisionSubmit = useCallback(async () => {
        if (!selectedRequestId || !selectedDecision) return;

        if (!adminRemarks.trim()) {
            alert("Remarks are mandatory when rejecting a cancellation request.");
            return;
        }

        try {
            setUpdatingId(selectedRequestId);

            const response = await leaveRequestApi.decideCancellation(selectedRequestId, {
                decision: selectedDecision,
                remarks: adminRemarks.trim(),
            });

            window.alert(
                response?.data?.message ||
                `Leave Cancellation request ${selectedDecision} successfully`
            );

            setRows(prev => prev.filter(r => r.__id !== selectedRequestId));

            if (onDecision) onDecision();


            setShowRemarksModal(false);

        } catch (err) {
            setError(err?.message || "Failed to process cancellation decision");
        } finally {
            setUpdatingId(null);
        }
    }, [selectedRequestId, selectedDecision, adminRemarks]);

    const printCancellationRequest = async (leaveId) => {
        try {
            const res = await axios.get(
                `${REACT_APP_API_URL}api/hrms/leave/cancellation/${leaveId}`
            );

            if (!res?.data?.data) {
                throw new Error("Invalid response");
            }

            generateLeaveCancellationPDF(res.data.data);

        } catch (err) {
            alert("Failed to generate cancellation PDF");
        }
    };

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            setLoading(true);
            setError("");

            try {
                const response =
                    await leaveRequestApi.getAllCancellationRequests();

                const items = Array.isArray(response?.data)
                    ? response.data
                    : [];

                if (!isMounted) return;

                const uiRows = [];
                const exportData = [];

                items.forEach((item) => {

                    uiRows.push({
                        __id: item.LeaveID,
                        employeeName: (
                            <span className="fw-semibold">
                                {item.EmployeeName}
                            </span>
                        ),
                        requestedOnRaw: item.CancellationRequestedDate,
                        requestedOn: (
                            <span className="text-muted small">
                                {formatDate(item.CancellationRequestedDate)}
                            </span>
                        ),
                        leaveType: (
                            <span className="badge bg-primary">
                                {item.LeaveType}
                            </span>
                        ),
                        from: formatDate(item.StartDate),
                        to: formatDate(item.EndDate),
                        totalDays: (
                            <span className="badge bg-info">
                                {item.TotalDays}
                            </span>
                        ),
                        reason: item.CancellationReason,
                        status: (
                            <span className="badge bg-warning text-dark">
                                Pending
                            </span>
                        ),
                        print: (
                            <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => printCancellationRequest(item.LeaveID)}
                                title="Print Cancellation Request"
                            >
                                <i className="bi bi-printer"></i>
                            </button>
                        ),
                        actions: renderActions(item.LeaveID),
                    });

                    exportData.push({
                        employeeName: item.EmployeeName,
                        requestedOn: formatDate(item.CancellationRequestedDate),
                        leaveType: item.LeaveType,
                        from: formatDate(item.StartDate),
                        to: formatDate(item.EndDate),
                        totalDays: item.TotalDays,
                        reason: item.CancellationReason,
                        status: "Pending",
                    });
                });

                setRows(uiRows);
                setExportRows(exportData);

            } catch (err) {
                if (!isMounted) return;
                setError(err?.message || "Failed to load cancellation requests");
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        load();
        return () => {
            isMounted = false;
        };
    }, []);

    const columns = [
        { Header: "Employee Name", accessor: "employeeName" },
        { Header: "Requested On", accessor: "requestedOn" },
        { Header: "Leave Type", accessor: "leaveType" },
        { Header: "From", accessor: "from" },
        { Header: "To", accessor: "to" },
        { Header: "Total Days", accessor: "totalDays" },
        { Header: "Cancellation Reason", accessor: "reason" },
        { Header: "Status", accessor: "status" },
        { Header: "Print", accessor: "print" },
        { Header: "Actions", accessor: "actions" },
    ];

    return (
        <>

            {/* <DataTableHeader columns={columns} data={exportRows} /> */}

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="leave-table-shell leave-admin-table-shell">
                <DataTable
                    columns={columns}
                    data={tableData}
                    loading={loading}
                    reportMode={true}
                    disableSorting={true}
                    enablePagination={false}
                />
            </div>

            {/* <DataTableFooter dataT={tableData} /> */}

            {showRemarksModal && (
                <>
                    <div className="modal show d-block leave-modal">
                        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        Reject Cancellation
                                    </h5>
                                    <button
                                        className="btn-close"
                                        onClick={() => setShowRemarksModal(false)}
                                    />
                                </div>

                                <div className="modal-body">
                                    <label className="form-label">
                                        Remarks
                                        {selectedDecision === "reject" && (
                                            <span className="text-danger"> *</span>
                                        )}
                                    </label>
                                    <textarea
                                        className="form-control"
                                        rows="4"
                                        value={adminRemarks}
                                        onChange={(e) => setAdminRemarks(e.target.value)}
                                        placeholder="Remarks are required"
                                    />
                                </div>

                                <div className="modal-footer">
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() => setShowRemarksModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className={`btn ${selectedDecision === "approve"
                                            ? "btn-success"
                                            : "btn-danger"
                                            }`}
                                        onClick={handleDecisionSubmit}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop show" />
                </>
            )}
        </>
    );
};

export default LeaveCancellationRequestTable;
